<template>
  <etapa-formulario>
    <transition name="tab-switch" mode="out-in">
      <div
        v-if="!modoFormulario"
        key="grid"
        class="etapa-formulario__campo etapa-formulario__campo--md-12 etapa-formulario__campo--xl-12"
      >
        <lista-terceiros
          @novo="novo"
          @editar="editar"
          @remover="remover"
          :terceiros="terceiros"
          @visualizar-terceiro="abrirDetalheTerceiro"
        />
      </div>

      <div v-else key="form" class="terceiros-form-wrap">
        <div
          class="terceiros-stepper"
          role="navigation"
          aria-label="Passos do envolvido"
        >
          <dados-progress-header
            :etapa-atual="passoMobile + 1"
            :total-etapas="passosTerceiros.length"
            :titulo="passosTerceiros[passoMobile].titulo"
            :preenchidos="totalPreenchidos"
            :total-geral="13"
          />
        </div>

        <formulario-terceiro
          @cancelar="cancelar"
          :validacao="validacao"
          @cep-input="onCepInput"
          :formularios="formularios"
          :passo-mobile="passoMobile"
          @atualizar-campo="atualizarCampo"
          @remover-formulario="removerFormulario"
          :carregando-endereco="carregandoEndereco"
          :opcoes-culpado="opcoesTerceirosEnvolvidos.culpado"
          :opcoes-tipo-lesao="opcoesTerceirosEnvolvidos.tipoLesao"
          :opcoes-arca-com-custo="opcoesTerceirosEnvolvidos.arcaComCusto"
        />
      </div>
    </transition>

    <ModalExcluirTerceiro
      v-model="mostrarModalExcluir"
      @cancelar="cancelarRemover"
      @confirmar="confirmarRemover"
    />
  </etapa-formulario>
</template>

<script lang="js">
import EtapaFormulario from "../EtapaFormulario.vue";
import ListaTerceiros from "../terceiros/ListaTerceiros.vue";
import FormularioTerceiro from "../terceiros/FormularioTerceiro.vue";
import ModalExcluirTerceiro from "../terceiros/ModalExcluirTerceiro.vue";
import DadosProgressHeader from "../DadosProgressHeader.vue";
import { aplicarMascaraCpfCnpj } from "../../masks/cpfCnpj.mask.js";
import { aplicarMascaraPlaca } from "../../masks/placa.mask.js";
import { aplicarMascaraTelefone, sanitizarTelefone } from "../../masks/telefone.mask.js";
import { aplicarMascaraCep, sanitizarCep } from "../../masks/cep.mask.js";
import { aplicarMascaraNome, normalizarNome } from "../../masks/nome.mask.js";
import {
  validarTerceirosFormularios,
  validarCampoTerceiro,
  deveValidarEmTempoReal,
  isValorInvalidoParaMascara
} from "../../../domain/validators/terceiros.validator.js";

const CAMPOS_POR_PASSO = [
  ["nome", "telefone", "cpf", "veiculo"],
  ["cep", "municipio", "bairro", "endereco", "numero", "complementos"],
  ["lesao", "culpado", "custos"]
];

const CAMPOS_FORMULARIO = [
  "nome", "telefone", "cpf", "veiculo",
  "cep", "municipio", "bairro", "logradouro", "endereco", "numero", "complementos",
  "lesao", "culpado", "custos"
];

/**
 * Etapa "Envolvimento de Terceiros" do cadastro/visualização de relato.
 *
 * Orquestra a listagem (`ListaTerceiros`) e o formulário (`FormularioTerceiro`) de
 * terceiros envolvidos. Em modo cadastro permite incluir/editar/remover; em
 * modo visualização (`inject modoVisualizacao=true` via `VisualizarRelatoOcorrenciaContainer`)
 * exibe a grid com botão `Visualizar` (evento `visualizar-terceiro`) que abre o
 * formulário em modo somente-leitura (`CampoTexto/CampoSelecao` desabilitados via `inject`).
 *
 * Estado interno:
 * - `modoFormulario=false` → lista; `true` → formulário (cadastro ou detalhe).
 * - `formularios` é array de rascunhos (1 por vez, criado via grid/`novo` ou `editar`).
 * - `indiceEdicao` guarda índice em edição; `null` em criação/visualização.
 *
 * @prop {Object} value - seção do relato (`ocorrencia.terceiros` no container) – `{ terceiros: Array<EnvolvidoView|TerceiroForm> }`
 * @prop {Array} [opcoesSelecione] - opções genéricas repassadas (legado)
 * @prop {Object} [opcoes] - opções genéricas (legado)
 * @prop {{houveLesao:Array, tipoLesao:Array, culpado:Array, arcaComCusto:Array}} [opcoesTerceirosEnvolvidos] - opções de selects vindas de `/terceiros-envolvidos-opcoes`
 * @prop {boolean} [carregandoEndereco=false] - loading da busca de CEP
 *
 * @inject {boolean} modoVisualizacao - `true` em `VisualizarRelatoOcorrenciaContainer` (provide), desabilita cadastro e ativa `abrirDetalheTerceiro`
 *
 * @emits input {Object} seção atualizada (`{ ...value, terceiros: [...] }`) a cada `aplicarFormularios`/`remover`
 * @emits salvar {Object} quando `EtapaFormulario` salva fora do modo formulário (ignorado em visualização)
 * @emits proxima - avança de aba (em visualização fecha detalhe se `modoFormulario=true`)
 * @emits buscar-endereco {Object} form, {string} cep - solicita busca de endereço ao container
 * @emits passo-mobile-terceiros {Object} `{indice, total}` - passo atual do formulário no mobile (navegação pelo footer do container, igual à aba Dados)
 * @emits rascunho-terceiros {Object} `{formularios, indiceEdicao, alterado}` - estado temporário do formulário emitido a cada alteração (`alterado` indica se algum campo mudou desde a abertura)
 */
export default {
  name: "EnvolvimentoTerceiros",

  inject: {
    modoVisualizacao: {
      type: Boolean,
      default: false
    }
  },

  components: {
    EtapaFormulario,
    FormularioTerceiro,
    ListaTerceiros,
    ModalExcluirTerceiro,
    DadosProgressHeader
  },

  props: {
    value: {
      type: Object,
      required: true
    },

    opcoesSelecione: {
      type: Array,
      default: () => []
    },

    opcoes: {
      type: Object,
      default: () => ({})
    },

    opcoesTerceirosEnvolvidos: {
      type: Object,
      default: () => ({ houveLesao: [], tipoLesao: [], culpado: [], arcaComCusto: [] })
    },

    carregandoEndereco: {
      type: Boolean,
      default: false
    },

    passoInicial: {
      type: Number,
      default: 0
    },

    formulariosIniciais: {
      type: Array,
      default: null
    },

    indiceEdicaoInicial: {
      type: Number,
      default: null
    },

    iniciarEmFormulario: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    modoFormulario(novoValor) {
      this.$emit("modo-formulario", novoValor);
    }
  },

  created() {
    const total = this.passosTerceiros.length;
    const inicial = Number.parseInt(this.passoInicial, 10);
    this.passoMobile = Number.isFinite(inicial)
      ? Math.min(Math.max(inicial, 0), total - 1)
      : 0;

    if (
      this.iniciarEmFormulario &&
      Array.isArray(this.formulariosIniciais) &&
      this.formulariosIniciais.length
    ) {
      try {
        this.formularios = JSON.parse(JSON.stringify(this.formulariosIniciais));
      } catch (_e) {
        this.formularios = [];
      }
      this.indiceEdicao =
        typeof this.indiceEdicaoInicial === "number"
          ? this.indiceEdicaoInicial
          : null;
      if (this.formularios.length) {
        this.validacao = {};
        this.formularioOriginal = this.clonarFormularios(this.formularios);
        this.modoFormulario = true;
      }
    }
  },

  data() {
    return {
      modoFormulario: false,
      formularios: [],formularioOriginal: null,
      indiceEdicao: null,
      passoMobile: 0,
      /** @type {Object<string, Object<string, string>>} */
      validacao: {},
      mostrarModalExcluir: false,
      indiceExcluir: null
    };
  },

  computed: {
    terceiros() {
      return this.value.terceiros || [];
    },

    /** Passos do formulário no mobile (mesmo padrão da aba Dados). */
    passosTerceiros() {
      return [
        { titulo: "Envolvido", descricao: "Identifique o envolvido no evento." },
        { titulo: "Endereço", descricao: "Informe onde o envolvido pode ser localizado." },
        { titulo: "Informações da Ocorrência", descricao: "Registre lesão, culpa e custos." }
      ];
    },

    totalPreenchidos() {
      const form = (this.formularios && this.formularios[0]) || {};
      const temValor = (val) => {
        if (val === null || val === undefined || val === "") return false;
        if (typeof val === "object") {
          const inner = val.value !== undefined ? val.value : val.label;
          return inner !== null && inner !== undefined && inner !== "";
        }
        return String(val).trim() !== "";
      };
      const campos = [
        "nome", "telefone", "cpf", "veiculo",
        "cep", "municipio", "bairro", "endereco", "numero", "complementos",
        "lesao", "culpado", "custos"
      ];
      return campos.filter((c) => temValor(form[c])).length;
    },

    fields() {
      const base = [
        {
          key: "seq",
          label: "Seq."
        },
        {
          key: "nome",
          label: "Nome"
        },
        {
          key: "telefone",
          label: "Telefone"
        },
        {
          key: "cpf",
          label: "CPF"
        },
        {
          key: "veiculo",
          label: "Veículo (Placa)"
        },
        {
          key: "lesao",
          label: "Lesão?",
          class: "text-center"
        },
        {
          key: "culpado",
          label: "Culpado(a)?",
          class: "text-center"
        },
        {
          key: "custos",
          label: "Custos?",
          class: "text-center"
        }
      ];
      if (!this.modoVisualizacao) {
        base.push({
          key: "acoes",
          label: "Ações",
          class: "text-right"
        });
      }
      return base;
    }
  },

  methods: {
    criarFormulario() {
      return {
        id: `${Date.now()}-${Math.random()}`,

        nome: "",
        telefone: "",
        cpf: "",
        veiculo: "",

        cep: "",
        municipio: "",
        bairro: "",
        logradouro: "",
        endereco: "",
        numero: "",
        complementos: "",

        lesao: null,
        culpado: null,
        custos: null
      };
    },

    novo() {
      if (this.modoVisualizacao) return;
      this.formularios = [this.criarFormulario()];
      this.formularioOriginal = this.clonarFormularios(this.formularios);
      this.indiceEdicao = null;
      this.validacao = {};
      this.passoMobile = 0;
      this.modoFormulario = true;
      this.notificarPassoTerceiros();
      this.notificarRascunho();

      this.$nextTick(() => {
        const input = this.$el.querySelector(
          ".terceiros-envolvidos__form input"
        );
        if (input) {
          input.focus();
        }
      });
    },

    removerFormulario(index) {
      if (this.formularios.length === 1) {
        this.cancelar();
        return;
      }

      this.formularios.splice(index, 1);
      this.notificarRascunho();
    },

    cancelar() {
      this.formularios = [];
      this.formularioOriginal = null;
      this.indiceEdicao = null;
      this.validacao = {};
      this.passoMobile = 0;
      this.modoFormulario = false;
    },

    aplicarMascaraCampo(campo, valor) {
      switch (campo) {
        case "nome":
          return aplicarMascaraNome(valor);
        case "cpf":
          return aplicarMascaraCpfCnpj(valor);
        case "telefone":
          return aplicarMascaraTelefone(valor);
        case "veiculo":
          return aplicarMascaraPlaca(valor);
        case "cep":
          return aplicarMascaraCep(valor);
        default:
          return valor;
      }
    },

    atualizarCampo(form, campo, valor) {
      const mascarado = isValorInvalidoParaMascara(campo, valor) ? valor : this.aplicarMascaraCampo(campo, valor);
      form[campo] = mascarado;

      const valorParaValidar = valor;
      const erro = validarCampoTerceiro(campo, valorParaValidar);
      const deveValidarAgora = deveValidarEmTempoReal(campo, valorParaValidar);

      if (erro && deveValidarAgora) {
        if (!this.validacao[form.id]) this.$set(this.validacao, form.id, {});
        this.$set(this.validacao[form.id], campo, erro);
      } else if (!erro) {
        if (this.validacao[form.id]) {
          this.$delete(this.validacao[form.id], campo);
          if (!Object.keys(this.validacao[form.id]).length) this.$delete(this.validacao, form.id);
        }
      } else {
        if (this.validacao[form.id] && this.validacao[form.id][campo]) {
          if (!erro.includes("Informe") && !erro.includes("incompleto")) {
            this.$delete(this.validacao[form.id], campo);
          }
        }
      }

      if (!erro && this.validacao[form.id]) {
        this.$delete(this.validacao[form.id], campo);
      }
      this.notificarRascunho();
    },

    onCepInput(form, valor) {
      if (isValorInvalidoParaMascara("cep", valor)) {
        form.cep = valor;
        const erroCep = validarCampoTerceiro("cep", valor);
        if (erroCep) {
          if (!this.validacao[form.id]) this.$set(this.validacao, form.id, {});
          this.$set(this.validacao[form.id], "cep", erroCep);
        }
        this.limparEndereco(form);
        this.notificarRascunho();
        return;
      }

      const mascarado = aplicarMascaraCep(valor);
      form.cep = mascarado;

      const erroCep = validarCampoTerceiro("cep", valor);
      const deveValidar = deveValidarEmTempoReal("cep", valor);
      if (erroCep && deveValidar) {
        if (!this.validacao[form.id]) this.$set(this.validacao, form.id, {});
        this.$set(this.validacao[form.id], "cep", erroCep);
      } else if (!erroCep && this.validacao[form.id]) {
        this.$delete(this.validacao[form.id], "cep");
        if (!Object.keys(this.validacao[form.id]).length) this.$delete(this.validacao, form.id);
      }

      if (mascarado && sanitizarCep(mascarado).length === 8) {
        this.buscarCep(form);
      } else {
        this.limparEndereco(form);
      }
      this.notificarRascunho();
    },

    limparEndereco(form) {
      form.endereco = "";
      form.bairro = "";
      form.municipio = "";
      form.complementos = "";
    },

    abrirDetalheTerceiro(terceiro, index) {
      if (!this.modoVisualizacao) return;
      this.formularios = [this.normalizar(terceiro)];
      this.formularioOriginal = this.clonarFormularios(this.formularios);
      this.indiceEdicao = index ?? null;
      this.validacao = {};
      this.passoMobile = 0;
      this.modoFormulario = true;
      this.notificarPassoTerceiros();
      this.notificarRascunho();
      this.rolarAoTopoPassoTerceiros();
    },

    notificarPassoTerceiros() {
      this.$emit("passo-mobile-terceiros", {
        indice: this.passoMobile,
        total: this.passosTerceiros.length
      });
    },

    rolarAoTopoPassoTerceiros() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".terceiros-stepper");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },

    validarPassoTerceiros(indice) {
      if (this.modoVisualizacao) return true;
      const campos = CAMPOS_POR_PASSO[indice] || [];
      const { erros } = validarTerceirosFormularios(this.formularios);
      let ok = true;
      this.formularios.forEach(form => {
        const errosForm = (erros && erros[form.id]) || {};
        const atual = { ...(this.validacao[form.id] || {}) };
        campos.forEach(campo => {
          delete atual[campo];
        });
        campos.forEach(campo => {
          if (errosForm[campo]) {
            atual[campo] = errosForm[campo];
            ok = false;
          }
        });
        if (Object.keys(atual).length) {
          this.$set(this.validacao, form.id, atual);
        } else {
          this.$delete(this.validacao, form.id);
        }
      });
      return ok;
    },

    avancarPasso() {
      if (!this.validarPassoTerceiros(this.passoMobile)) {
        this.rolarAoTopoPassoTerceiros();
        return;
      }
      if (this.passoMobile < this.passosTerceiros.length - 1) {
        this.passoMobile += 1;
        this.rolarAoTopoPassoTerceiros();
        this.notificarPassoTerceiros();
      }
    },
    voltarPasso() {
      if (this.passoMobile > 0) {
        this.passoMobile -= 1;
        this.rolarAoTopoPassoTerceiros();
        this.notificarPassoTerceiros();
      }
    },

    salvar() {
      if (this.modoVisualizacao) {
        if (this.modoFormulario) {
          this.cancelar();
        }
        return;
      }
      if (this.modoFormulario) {
        if (this.validarFormularios()) {
          const total = this.formularios.length;
          this.aplicarFormularios();

          this.$bvToast.toast(
            `${total} envolvido(s) salvo(s) com sucesso.`,
            {
              title: "Sucesso",
              variant: "success",
              solid: true
            }
          );
        }
        return;
      }

      this.$emit("salvar", this.value);
    },

    proximaAba() {
      if (this.modoVisualizacao) {
        if (this.modoFormulario) {
          this.cancelar();
          return;
        }
        this.$emit("proxima");
        return;
      }
      if (this.modoFormulario) {
        if (this.validarFormularios()) {
          this.aplicarFormularios();
          this.$emit("proxima");
        }
        return;
      }

      this.$emit("proxima");
    },

    validarFormularios() {
      const { isValid, erros } = validarTerceirosFormularios(this.formularios);
      this.validacao = erros;
      return isValid;
    },

    aplicarFormularios() {
      const lista = [...this.terceiros];
      const novos = this.formularios.map(form => {
        const clone = { ...form };
        clone.nome = normalizarNome(clone.nome);
        clone.telefone = sanitizarTelefone(clone.telefone);
        clone.cep = sanitizarCep(clone.cep);
        return clone;
      });

      if (this.indiceEdicao !== null) {
        lista.splice(this.indiceEdicao, 1, ...novos);
      } else {
        lista.push(...novos);
      }

      this.$emit("input", {
        ...this.value,
        terceiros: lista
      });

      this.formularioOriginal = this.clonarFormularios(this.formularios);
      this.notificarRascunho();
    },

    editar(terceiro, index) {
      if (this.modoVisualizacao) return;
      this.formularios = [this.normalizar(terceiro)];
      this.formularioOriginal = this.clonarFormularios(this.formularios);
      this.indiceEdicao = index;
      this.validacao = {};
      this.passoMobile = 0;
      this.modoFormulario = true;
      this.notificarPassoTerceiros();
      this.notificarRascunho();
    },

    /**
     * @emits rascunho-terceiros
     */
    notificarRascunho() {
      this.$emit("rascunho-terceiros", {
        formularios: this.formularios,
        indiceEdicao: this.indiceEdicao,
        alterado: this.formularioAlterado()
      });
    },

    clonarFormularios(formularios) {
      try {
        return JSON.parse(JSON.stringify(formularios || []));
      } catch (_e) {
        return null;
      }
    },

    valorParaComparacao(valor) {
      if (valor === null || valor === undefined) return "";
      if (typeof valor === "object") {
        const interno = valor.value !== undefined ? valor.value : valor.label;
        return interno === null || interno === undefined
          ? ""
          : String(interno).trim();
      }
      return String(valor).trim();
    },

    /**
     * @returns {boolean}
     */
    formularioAlterado() {
      const original = this.formularioOriginal;
      if (!original) return false;
      const atual = Array.isArray(this.formularios) ? this.formularios : [];
      if (atual.length !== original.length) return true;
      return atual.some((form, indice) => {
        const base = original[indice] || {};
        return CAMPOS_FORMULARIO.some(
          campo =>
            this.valorParaComparacao(form?.[campo]) !==
            this.valorParaComparacao(base?.[campo])
        );
      });
    },

    normalizar(terceiro) {
      const form = {
        ...this.criarFormulario(),
        ...terceiro
      };

      if (form.nome) form.nome = normalizarNome(form.nome);
      if (form.cpf) form.cpf = aplicarMascaraCpfCnpj(form.cpf);
      if (form.telefone) form.telefone = aplicarMascaraTelefone(form.telefone);
      const placaPura = form.placa || form.nrPlaca || "";
      if (placaPura && String(placaPura).replace(/[^A-Za-z0-9]/g, "").length >= 7) {
        form.veiculo = aplicarMascaraPlaca(placaPura);
      } else if (form.veiculo) {
        const rawVeiculo = String(form.veiculo);
        const m = rawVeiculo.match(/([A-Z]{3}[0-9][A-Z0-9][0-9]{2})/i);
        if (m) {
          form.veiculo = aplicarMascaraPlaca(m[1]);
        } else {
          const placaCandidata = String(form.veiculo).replace(/[^A-Za-z0-9]/g, "");
          form.veiculo = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/i.test(placaCandidata) ? aplicarMascaraPlaca(placaCandidata) : "";
        }
      }
      if (form.cep) form.cep = aplicarMascaraCep(form.cep);
      return form;
    },

    remover(index) {
      if (this.modoVisualizacao) return;
      this.indiceExcluir = index;
      this.mostrarModalExcluir = true;
    },

    confirmarRemover() {
      const index = this.indiceExcluir;
      if (index == null) return;
      const itemRemovido = this.terceiros[index];
      const lista = [...this.terceiros];
      lista.splice(index, 1);
      this.$emit("input", {
        ...this.value,
        terceiros: lista
      });
      const sqEnvolvido = itemRemovido?.sqEnvolvido ?? itemRemovido?.id;
      const isPersistido = sqEnvolvido != null && String(sqEnvolvido).match(/^\d+$/);
      if (isPersistido) {
        this.$emit("excluir-envolvido", { sqEnvolvido: Number(sqEnvolvido), item: itemRemovido, index });
      }
      this.mostrarModalExcluir = false;
      this.indiceExcluir = null;
    },

    cancelarRemover() {
      this.mostrarModalExcluir = false;
      this.indiceExcluir = null;
    },

    async buscarCep(form) {
      if (!form.cep) return;

      const cepFormatado = form.cep.replace(/\D/g, "");
      if (cepFormatado.length !== 8) return;

      this.$emit("buscar-endereco", form, cepFormatado);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/_main";
@import "../../../styles/components/_terceiros";
@import "../../../styles/layout/_transitions";
</style>



